import { getUserUuidFromToken } from 'Shared/utils';
import type { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import axios, { isAxiosError } from 'axios';
import dayjs from 'dayjs';
import type { RefreshDataDto, RefreshOkDto } from 'viewup-api';
import { RefreshUserTokenUseCaseExceptionCode } from 'viewup-api/v2';
import type { TokenRepository, V2ErrorResponse } from 'viewup-lib/common';
import {
  AuthInterceptorTemplate,
  AuthorizationError,
  clientTypeHeaders,
  getLocalStorage,
  getSessionStorage,
  REFRESHING,
  removeLocalStorage,
  setLocalStorage,
  TOKEN_NOT_EXIST,
} from 'viewup-lib/common';
import { authGate } from './authGate';
import { tokenRepository } from './httpClient';
import { attachClientTelemetry } from './telemetryInterceptor';

type RetriableRequestConfig = InternalAxiosRequestConfig & { __authRetried?: boolean };

export class AuthInterceptor extends AuthInterceptorTemplate {
  static REFRESH_TIME_KEY = 'refreshTime';

  private instance?: AxiosInstance;

  constructor(tokenRepository: TokenRepository<true>, refreshUrl: string) {
    super(tokenRepository as unknown as TokenRepository<false>, refreshUrl);
  }

  // 응답 측 재시도가 이 인스턴스를 우선 사용한다. 미주입이면 기존 bare axios 폴백 유지.
  attachInstance(instance: AxiosInstance): void {
    this.instance = instance;
  }

  override get requestInterceptorOnFulfilled(): (
    config: InternalAxiosRequestConfig,
  ) => Promise<InternalAxiosRequestConfig> {
    return async (config: InternalAxiosRequestConfig) => {
      // refresh 요청 자신은 게이트를 건너뛴다. 이 early return이 없으면 데드락이다 —
      // refreshAxios가 이 오버라이드된 getter를 등록하므로 refresh 요청 자신이
      // waitForRefresh()를 기다리게 된다.
      if (config.url === this.refreshTokenUrl) {
        return super.requestInterceptorOnFulfilled(config);
      }

      if (authGate.isUnauthorized()) {
        throw new AuthorizationError(TOKEN_NOT_EXIST);
      }

      await authGate.waitForRefresh();

      if (authGate.isUnauthorized()) {
        throw new AuthorizationError(TOKEN_NOT_EXIST);
      }

      let injected = await super.requestInterceptorOnFulfilled(config);
      const token = injected.headers.Authorization?.toString().replace('Bearer ', '');

      if (token && authGate.isDead(token)) {
        const refreshed = await this.ensureRefresh(config.baseURL ?? '');

        // 반환값을 버리면 안 된다. `runRefresh` 슬롯은 두 transport가 공유하므로,
        // 오토뷰잉 쪽이 먼저 슬롯을 잡고 빈 토큰으로 물러난 경우 여기서는
        // `isUnauthorized() === false`만 보고 통과해 **여전히 dead인 같은 토큰**을
        // 재주입한 채 요청을 발사하게 된다.
        if (!refreshed) throw this.refreshFailureError();

        injected = await super.requestInterceptorOnFulfilled(config);

        const reinjected = injected.headers.Authorization?.toString().replace('Bearer ', '');
        if (reinjected && authGate.isDead(reinjected)) throw this.refreshFailureError();
      }

      return injected;
    };
  }

  override get responseInterceptorOnRejected(): (error: unknown) => Promise<never> {
    // 부모 클래스는 항상 reject(Promise<never>)하지만, 재시도 성공 시 resolve가 필요하므로 타입 단언 사용
    return (async (error: unknown) => {
      if (!isAxiosError(error)) return Promise.reject(error);

      const token = await this.tokenRepository.getToken();
      if (!token) {
        return Promise.reject(error);
      }

      try {
        this.handleNonAuthError(error, this.tokenRepository);
      } catch (e) {
        return Promise.reject(e);
      }

      const originalRequest = error.config as RetriableRequestConfig | undefined;
      if (!originalRequest) return Promise.reject(error);

      // 이미 재시도한 요청이 다시 401을 받으면 refresh를 다시 트리거하지 않고 즉시 포기 — 무한 루프 차단
      if (originalRequest.__authRetried) {
        return Promise.reject(new AuthorizationError(TOKEN_NOT_EXIST, error));
      }

      // 요청에 사용된 토큰과 현재 저장된 토큰 비교
      // 다르면 이미 refresh가 완료된 것이므로 새 토큰으로 재시도만 하면 됨
      const currentToken = await this.tokenRepository.getToken();
      const requestToken = originalRequest.headers.Authorization?.toString().replace('Bearer ', '');

      if (requestToken && currentToken && requestToken !== currentToken) {
        // 이 분기에도 상한을 둔다. 무한 루프는 아니지만(회차마다 별도의 refresh 성공이
        // 필요하다) 한 요청이 두 번 이상 재시도되는 걸 막는 가드가 여기만 비어 있었다.
        originalRequest.__authRetried = true;
        originalRequest.headers.Authorization = `Bearer ${currentToken}`;
        return this.instance?.request(originalRequest) ?? axios(originalRequest);
      }

      if (requestToken) {
        authGate.markDead(requestToken);
      }

      const accessToken = await this.ensureRefresh(originalRequest.baseURL ?? '');

      if (!accessToken) {
        return Promise.reject(this.refreshFailureError(error));
      }

      // 원래 실패한 요청을 새 토큰으로 재시도
      originalRequest.__authRetried = true;
      originalRequest.headers.Authorization = `Bearer ${accessToken}`;
      return this.instance?.request(originalRequest) ?? axios(originalRequest);
    }) as (error: unknown) => Promise<never>;
  }

  /**
   * refresh 실패를 나타내는 에러. **최종 실패와 판단 보류를 다른 타입으로 낸다.**
   *
   * `TOKEN_NOT_EXIST`는 `QueryProvider`가 `LINK.HOME`으로 하드 리다이렉트하는 신호다.
   * 보류(디바운스로 시도조차 못 함)에까지 이걸 쓰면 멀쩡한 세션이 로그인 화면으로 튕긴다.
   * 보류는 `REFRESHING`으로 낸다 — `defaultQueryPolicy.retryCondition`이 이 값만 retry
   * 대상으로 보므로, React Query가 디바운스 창이 지난 뒤 알아서 다시 시도한다.
   *
   * 판별은 인스턴스 필드가 아니라 `authGate`의 공유 상태로 한다. `runRefresh` 프로미스를
   * 여러 호출자가 나눠 갖기 때문에 필드를 읽으면 호출자마다 답이 갈릴 수 있다.
   */
  private refreshFailureError(cause?: unknown): AuthorizationError {
    return authGate.isUnauthorized()
      ? new AuthorizationError(TOKEN_NOT_EXIST, cause)
      : new AuthorizationError(REFRESHING, cause);
  }

  // authGate single-flight를 통해 refresh를 진행한다. 성공하면 저장 후 게이트를 리셋하고,
  // 최종 실패면 unauthorized로 표시한 뒤 토큰을 지운다. 보류면 게이트를 건드리지 않는다.
  private async ensureRefresh(baseURL: string): Promise<string> {
    return authGate.runRefresh(async () => {
      const accessToken = await this.refreshTokenWithRetry(baseURL);

      if (accessToken) {
        await this.tokenRepository.saveToken(accessToken);
        // 성공했으면 디바운스 창을 닫는다. 중복 refresh 방지는 이제 `runRefresh`(realm 내)와
        // `navigator.locks`(realm 간)가 맡으므로, 여기 5초 냉각을 남겨두면 새 토큰이 그 창
        // 안에서 401을 받았을 때 refresh를 한 번도 못 보내고 로그아웃으로 직행한다.
        this.removeRefreshTokenRequestTime();
        authGate.reset();
        return accessToken;
      }

      // 보류 — 인증 만료의 증거가 없으므로 게이트를 잠그지 않는다. 이 요청만 실패하고
      // 다음 요청이 다시 시도한다. 판정은 `authGate`의 공유 상태로 한다: `runRefresh` 슬롯을
      // 두 transport가 나눠 쓰므로, 저쪽 fn이 슬롯을 잡고 물러난 경우 이쪽 인스턴스 필드로는
      // 그 사실을 알 수 없다.
      if (authGate.wasRefreshDeferred()) return '';

      authGate.markUnauthorized();
      await this.tokenRepository.removeToken();
      return '';
    });
  }

  private async refreshTokenWithRetry(baseURL: string): Promise<string> {
    let errorCount = 0;

    const refreshAxios = axios.create({
      baseURL,
      headers: { ...clientTypeHeaders },
      withCredentials: true,
    });
    // 이것도 요청을 내보내는 transport다. refresh 실패는 리포트 대상 에러의 핵심인데
    // breadcrumb이 없으면 직후 리포트가 낡은 트레이스에 오귀속된다.
    // 등록 순서: request는 LIFO라 토큰 주입 이후에, response는 FIFO라 가장 먼저 관측하도록 여기.
    attachClientTelemetry(refreshAxios);
    refreshAxios.interceptors.request.use(this.requestInterceptorOnFulfilled);

    const refresh: () => Promise<string> = async () => {
      if (errorCount >= 3) return '';

      try {
        const { accessToken } = await this.requestAccessToken(refreshAxios, this.refreshTokenUrl);
        return accessToken;
      } catch (e) {
        if (this.handleRefreshExpireError(e) === 'STOP') return '';

        errorCount++;
        this.removeRefreshTokenRequestTime();
        return await refresh();
      }
    };

    return await refresh();
  }

  protected override handleNonAuthError(error: unknown, tokenRepository: TokenRepository): void {
    if (isAxiosError(error)) {
      const err = error as AxiosError<V2ErrorResponse>;

      if (
        err.config?.url === '/v2/auth/refresh' &&
        err.response?.data.code === RefreshUserTokenUseCaseExceptionCode.Unauthorized
      ) {
        tokenRepository.removeToken();
        throw new AuthorizationError(TOKEN_NOT_EXIST);
      }

      if (err.config?.url === '/v2/users/me' && err.response?.status === 404) {
        tokenRepository.removeToken();
        throw new AuthorizationError(TOKEN_NOT_EXIST);
      }

      if (err.config?.url === '/v2/auth/login') throw error;

      if (
        !(
          err.response?.data.code === RefreshUserTokenUseCaseExceptionCode.Unauthorized ||
          err.response?.data.code === 'auth-009'
        )
      )
        throw error;
      return;
    }

    throw error;
  }

  protected override handleRefreshExpireError(error: unknown): 'STOP' | 'ONGOING' {
    const err = error as AxiosError<V2ErrorResponse>;

    if (
      err.config?.url === '/v2/auth/refresh' &&
      err.response?.data.code === RefreshUserTokenUseCaseExceptionCode.Unauthorized
    )
      return 'STOP';
    return 'ONGOING';
  }

  protected override async requestAccessToken(
    httpClient: AxiosInstance,
    refreshTokenUrl: string,
  ): Promise<{ accessToken: string }> {
    const lock = await this.executeRefreshToken(httpClient, refreshTokenUrl);
    return lock as RefreshDataDto;
  }

  private async executeRefreshToken(client: AxiosInstance, refreshTokenUrl: string) {
    if (!this.isAvailableRefreshRequest()) {
      const current = await tokenRepository.getToken();

      // 다른 realm이 방금 갱신해 둔 토큰이면 그대로 쓴다.
      if (current && !authGate.isDead(current)) return { accessToken: current };

      // 저장 토큰도 dead인데 디바운스에 막혔다 — HTTP를 아예 못 보냈으므로 판단 보류다.
      // 만료 토큰을 새 토큰인 척 반환하지도, 인증 만료로 단정하지도 않는다.
      authGate.markRefreshDeferred();
      return { accessToken: '' };
    }

    if (!navigator.locks) {
      console.warn(
        'Web Locks API is not supported in this browser. Falling back to a simpler lock or no lock.',
      );
      const token = await tokenRepository.getToken();
      const { data } = await client.get<RefreshOkDto>(refreshTokenUrl, {
        headers: {
          'x-httpClient': 'new',
          'x-requester-id': getUserUuidFromToken(token)?.userUuid,
          'x-tab-id': getSessionStorage('tabId'),
        },
      });
      return data.data;
    }

    // 서버 컴포넌트 경로는 이 클래스에 도달하지 않는다 — `httpClient.ts`가 SSR 인스턴스에
    // `[ssrAuthInterceptor, ssrErrorInterceptor]`만 등록하고, `SsrAuthInterceptor`는
    // `requestAccessToken`을 오버라이드해 이 메서드 자체를 대체한다. `navigator`·`sessionStorage`
    // 접근이 여기 있어도 안전한 이유. (SD-4485 조사에서 확인)
    return navigator.locks.request(refreshTokenUrl, async (lock) => {
      if (!lock) {
        // 락을 못 잡았다 — 시도조차 못 한 것이므로 최종 실패가 아니라 보류다.
        authGate.markRefreshDeferred();
        return { accessToken: '' };
      }

      const token = await tokenRepository.getToken();

      // 락 획득 직전 다른 realm이 이미 refresh를 마쳤을 수 있다 — dead가 아니면
      // 저장된 토큰을 그대로 반환하고 HTTP 호출을 생략한다.
      if (!authGate.isDead(token)) {
        return { accessToken: token };
      }

      const { data } = await client.get<RefreshOkDto>(refreshTokenUrl, {
        headers: {
          'x-httpClient': 'new',
          'x-requester-id': getUserUuidFromToken(token)?.userUuid,
          'x-tab-id': getSessionStorage('tabId'),
        },
      });
      return data.data;
    });
  }

  protected override isAvailableRefreshRequest(): boolean {
    const originRule = super.isAvailableRefreshRequest();

    try {
      const recordedTime = getLocalStorage<number>(AuthInterceptor.REFRESH_TIME_KEY);
      const customRule =
        recordedTime === null ? true : dayjs().diff(dayjs(recordedTime), 'seconds') > 5;

      return originRule && customRule;
    } catch (error) {
      console.error('AuthInterceptor - isAvailableRefreshRequest', error);
      return originRule;
    }
  }

  protected override recordRefreshTokenRequestTime(): void {
    // this.requestTimetable[this.refreshTokenUrl] = dayjs().toDate();
    try {
      setLocalStorage(AuthInterceptor.REFRESH_TIME_KEY, dayjs().toDate().getTime());
    } catch (error) {
      console.error('AuthInterceptor - recordRefreshTokenRequestTime', error);
    }
  }

  protected override removeRefreshTokenRequestTime(): void {
    // this.requestTimetable[this.refreshTokenUrl] = undefined;
    try {
      removeLocalStorage(AuthInterceptor.REFRESH_TIME_KEY);
    } catch (error) {
      console.error('AuthInterceptor - removeRefreshTokenRequestTime', error);
    }
  }
}
